import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Mail, Phone, Building2, Shield, Clock, Activity, LogOut, Check, X, Pencil, Key, Eye, EyeOff, CheckCircle, Plus, Search, Info, Unlock, Lock } from 'lucide-react';
import api from '../../services/api';
import { useAuthStore } from '../../context/authStore';
import CwMemberMappingCard from '../../components/admin/CwMemberMappingCard';

const roleBadges = {
  admin: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300',
  technician: 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300',
  company_admin: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300',
  finance: 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300',
  billing: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300',
  team_member: 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300',
  end_user: 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300',
  user: 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300',
};

const actionLabels = {
  'register': 'Registered',
  'login': 'Logged in',
  'logout': 'Logged out',
  'role_changed': 'Role changed',
  'password_reset': 'Password reset',
  'password_reset_admin': 'Password reset by admin',
  'password_change': 'Password changed',
  'session_revoked': 'Session revoked',
  'user_updated': 'Profile updated',
  'email_verified': 'Email verified',
  'account_unlocked': 'Account unlocked by admin',
  'user_deactivated': 'Removed from the portal',
  'user_reactivated': 'Restored to the portal',
};

/**
 * What this person can actually see, said in a sentence.
 *
 * An empty list means one of two opposite things — every company, or none —
 * and which one depends on whether their role carries `all_companies`, which
 * is not visible from this screen. Leaving the reader to infer it from an
 * empty list makes the dangerous reading the natural one: a blank list looks
 * locked down and can mean the opposite.
 *
 * An explicit list always wins, including over `all_companies`.
 */
function CompanyAccessSummary({ count, isInternalStaff, hasAllCompanies }) {
  if (count > 0) {
    return (
      <p className="text-sm text-neutral-500 dark:text-neutral-400">
        Sees <span className="font-medium text-neutral-700 dark:text-neutral-200">
          {count} {count === 1 ? 'company' : 'companies'}
        </span>{isInternalStaff ? ' in the admin section' : ''}, and no others.
      </p>
    );
  }

  if (!isInternalStaff) {
    return (
      <p className="text-sm text-neutral-500 dark:text-neutral-400">
        Sees their primary company only.
      </p>
    );
  }

  if (hasAllCompanies) {
    return (
      <p className="text-sm text-amber-700 dark:text-amber-400">
        Sees <span className="font-medium">every company</span> — their role allows it and nothing here restricts
        them. Add a company to limit them to it.
      </p>
    );
  }

  return (
    <p className="text-sm text-amber-700 dark:text-amber-400">
      Sees <span className="font-medium">no client data at all</span> — nothing is listed and their role does not
      allow every company. Add the companies they should work on.
    </p>
  );
}

export default function UserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();

  const [user, setUser] = useState(null);
  const [activity, setActivity] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Role change state
  const [showRoleModal, setShowRoleModal] = useState(false);
  // Every role the tenant defines (staff, built-in customer roles, custom ones),
  // from the server; the old fixed list could not offer Finance, Billing,
  // Team Member, End User or a custom role.
  const [allRoles, setAllRoles] = useState([]);
  useEffect(() => {
    if (allRoles.length) return;
    api.get('/admin/roles').then((r) => setAllRoles(r.data.roles || [])).catch(() => {});
  }, [allRoles.length]);
  // The roles a customer may hold at an additional company (Company Access).
  const customerRoles = allRoles.filter((r) => !r.isInternal);
  const roleLabel = (name) => allRoles.find((r) => r.name === name)?.displayName || String(name || 'end_user').replace(/_/g, ' ');
  const [selectedRole, setSelectedRole] = useState('');
  const [updatingRole, setUpdatingRole] = useState(false);

  // Session revoke state
  const [revokeAllModal, setRevokeAllModal] = useState(false);
  const [revokingAll, setRevokingAll] = useState(false);

  // Edit profile state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '', phone: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  // Reset password state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Unlock account state
  const [unlocking, setUnlocking] = useState(false);

  // Company access state
  const [companyAccess, setCompanyAccess] = useState([]);
  const [hasAllCompanies, setHasAllCompanies] = useState(false);
  const [loadingCompanyAccess, setLoadingCompanyAccess] = useState(false);
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const [companySearchResults, setCompanySearchResults] = useState([]);
  const [searchingCompanies, setSearchingCompanies] = useState(false);
  const [addingCompany, setAddingCompany] = useState(false);

  // Location access state
  const [locationAccess, setLocationAccess] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [loadingLocationAccess, setLoadingLocationAccess] = useState(false);
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [addingLocation, setAddingLocation] = useState(false);

  // Board access state
  const [boardAccess, setBoardAccess] = useState([]);
  const [availableBoards, setAvailableBoards] = useState([]);
  const [loadingBoardAccess, setLoadingBoardAccess] = useState(false);
  const [showAddBoardModal, setShowAddBoardModal] = useState(false);
  const [addingBoard, setAddingBoard] = useState(false);

  useEffect(() => {
    fetchUserData();
  }, [userId]);

  const fetchUserData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [userRes, activityRes, sessionsRes] = await Promise.all([
        api.get(`/admin/users/${userId}`),
        api.get(`/admin/users/${userId}/activity`),
        api.get(`/admin/users/${userId}/sessions`),
      ]);

      setUser(userRes.data.user);
      setActivity(activityRes.data.activity);
      setSessions(sessionsRes.data.sessions);
      setSelectedRole(userRes.data.user.role);
      setEditForm({
        firstName: userRes.data.user.first_name || '',
        lastName: userRes.data.user.last_name || '',
        phone: userRes.data.user.phone || '',
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async () => {
    if (!selectedRole || selectedRole === user.role) return;

    setUpdatingRole(true);
    try {
      await api.patch(`/admin/users/${userId}/role`, { role: selectedRole });
      setUser({ ...user, role: selectedRole });
      setShowRoleModal(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update role');
    } finally {
      setUpdatingRole(false);
    }
  };

  const handleRevokeAllSessions = async () => {
    setRevokingAll(true);
    try {
      await api.delete(`/admin/users/${userId}/sessions`);
      setSessions([]);
      setRevokeAllModal(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to revoke sessions');
    } finally {
      setRevokingAll(false);
    }
  };

  const handleEditProfile = async () => {
    setSavingEdit(true);
    setError(null);
    try {
      const response = await api.patch(`/admin/users/${userId}`, editForm);
      setUser(response.data.user);
      setShowEditModal(false);
      // Refresh activity
      const activityRes = await api.get(`/admin/users/${userId}/activity`);
      setActivity(activityRes.data.activity);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleResetPassword = async () => {
    if (passwordForm.password !== passwordForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (passwordForm.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setSavingPassword(true);
    setError(null);
    try {
      await api.post(`/admin/users/${userId}/reset-password`, {
        password: passwordForm.password,
      });
      setPasswordSuccess(true);
      setSessions([]); // Sessions were revoked
      // Refresh activity
      const activityRes = await api.get(`/admin/users/${userId}/activity`);
      setActivity(activityRes.data.activity);
      // Auto close after 2 seconds
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordSuccess(false);
        setPasswordForm({ password: '', confirmPassword: '' });
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleUnlockAccount = async () => {
    setUnlocking(true);
    setError(null);
    try {
      await api.post(`/admin/users/${userId}/unlock`);
      setUser({ ...user, failed_login_attempts: 0 });
      // Refresh activity
      const activityRes = await api.get(`/admin/users/${userId}/activity`);
      setActivity(activityRes.data.activity);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to unlock account');
    } finally {
      setUnlocking(false);
    }
  };

  // Fetch company access for internal staff
  const fetchCompanyAccess = async () => {
    setLoadingCompanyAccess(true);
    try {
      const res = await api.get(`/admin/users/${userId}/company-access`);
      setCompanyAccess(res.data.companies || []);
      setHasAllCompanies(res.data.hasAllCompanies || false);
    } catch (err) {
      console.error('Failed to fetch company access:', err);
    } finally {
      setLoadingCompanyAccess(false);
    }
  };

  // Fetch location access for internal staff
  const fetchLocationAccess = async () => {
    setLoadingLocationAccess(true);
    try {
      const [accessRes, locationsRes] = await Promise.all([
        api.get(`/admin/users/${userId}/location-access`),
        api.get('/admin/locations')
      ]);
      setLocationAccess(accessRes.data.locations || []);
      setAvailableLocations(locationsRes.data.locations || []);
    } catch (err) {
      console.error('Failed to fetch location access:', err);
    } finally {
      setLoadingLocationAccess(false);
    }
  };

  // Fetch board access for internal staff
  const fetchBoardAccess = async () => {
    setLoadingBoardAccess(true);
    try {
      const [accessRes, boardsRes] = await Promise.all([
        api.get(`/admin/users/${userId}/board-access`),
        api.get('/admin/boards')
      ]);
      setBoardAccess(accessRes.data.boards || []);
      setAvailableBoards(boardsRes.data.boards || []);
    } catch (err) {
      console.error('Failed to fetch board access:', err);
    } finally {
      setLoadingBoardAccess(false);
    }
  };

  // Effect to load company, location, and board access when user data loads
  useEffect(() => {
    if (user) {
      // Company access for all users (customer users can have multi-company access)
      fetchCompanyAccess();
      // Location and board access only for internal staff
      if (user.is_internal_staff) {
        fetchLocationAccess();
        fetchBoardAccess();
      }
    }
  }, [user?.id, userId]);

  // Search companies directly from ConnectWise
  const searchCompanies = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setCompanySearchResults([]);
      return;
    }
    setSearchingCompanies(true);
    try {
      const res = await api.get(`/admin/companies/search-cw?q=${encodeURIComponent(searchQuery)}&limit=10`);
      // Filter out already assigned companies and user's primary company
      const assignedIds = companyAccess.map(c => c.cwCompanyId);
      const primaryCompanyId = user?.cw_company_id;
      const filtered = (res.data.companies || []).filter(c => {
        const companyId = c.cwCompanyId || c.cw_company_id || c.id;
        return !assignedIds.includes(companyId) && companyId !== primaryCompanyId;
      });
      setCompanySearchResults(filtered);
    } catch (err) {
      console.error('Failed to search companies:', err);
    } finally {
      setSearchingCompanies(false);
    }
  };

  // Debounced company search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (companySearchQuery) {
        searchCompanies(companySearchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [companySearchQuery]);

  // Add company access
  const handleAddCompany = async (company) => {
    setAddingCompany(true);
    try {
      await api.post(`/admin/users/${userId}/company-access`, {
        cwCompanyId: company.cwCompanyId || company.cw_company_id || company.id,
        companyName: company.companyName || company.company_name || company.name
      });
      await fetchCompanyAccess();
      setCompanySearchQuery('');
      setCompanySearchResults([]);
      setShowAddCompanyModal(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add company access');
    } finally {
      setAddingCompany(false);
    }
  };

  // Remove company access
  const handleRemoveCompany = async (cwCompanyId) => {
    try {
      await api.delete(`/admin/users/${userId}/company-access/${cwCompanyId}`);
      setCompanyAccess(companyAccess.filter(c => c.cwCompanyId !== cwCompanyId));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove company access');
    }
  };

  // The customer role held at one additional company (null = their own role)
  const handleCompanyRoleChange = async (cwCompanyId, role) => {
    try {
      await api.patch(`/admin/users/${userId}/company-access/${cwCompanyId}`, { role });
      setCompanyAccess(companyAccess.map((c) => (c.cwCompanyId === cwCompanyId ? { ...c, role } : c)));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to set the role at this company');
    }
  };

  // Set a company as the user's primary company (for customer users)
  const handleSetPrimaryCompany = async (cwCompanyId, companyName) => {
    try {
      await api.patch(`/admin/users/${userId}/primary-company`, {
        companyId: cwCompanyId,
        companyName: companyName
      });
      // Refresh user data to show the updated primary company
      const response = await api.get(`/admin/users/${userId}`);
      setUser(response.data);
      // The old primary company should now appear in company access list
      await fetchCompanyAccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to set primary company');
    }
  };

  // Add location access
  const handleAddLocation = async (location) => {
    setAddingLocation(true);
    try {
      await api.post(`/admin/users/${userId}/location-access`, {
        cwLocationId: location.id,
        locationName: location.name
      });
      await fetchLocationAccess();
      setShowAddLocationModal(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add location access');
    } finally {
      setAddingLocation(false);
    }
  };

  // Remove location access
  const handleRemoveLocation = async (cwLocationId) => {
    try {
      await api.delete(`/admin/users/${userId}/location-access/${cwLocationId}`);
      setLocationAccess(locationAccess.filter(l => l.cwLocationId !== cwLocationId));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove location access');
    }
  };

  // Add board access
  const handleAddBoard = async (board) => {
    setAddingBoard(true);
    try {
      await api.post(`/admin/users/${userId}/board-access`, {
        cwBoardId: board.id,
        boardName: board.name
      });
      await fetchBoardAccess();
      setShowAddBoardModal(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add board access');
    } finally {
      setAddingBoard(false);
    }
  };

  // Remove board access
  const handleRemoveBoard = async (cwBoardId) => {
    try {
      await api.delete(`/admin/users/${userId}/board-access/${cwBoardId}`);
      setBoardAccess(boardAccess.filter(b => b.cwBoardId !== cwBoardId));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove board access');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTimeRemaining = (expireDate) => {
    const now = new Date();
    const expire = new Date(expireDate);
    const diff = expire - now;

    if (diff <= 0) return 'Expired';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }

    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !user) {
    const handleBack = () => {
      const returnUrl = sessionStorage.getItem('adminUsersReturnUrl');
      sessionStorage.removeItem('adminUsersReturnUrl');
      navigate(returnUrl || '/admin/users', { state: { refresh: Date.now() } });
    };

    return (
      <div className="space-y-4">
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Users
        </button>
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3 text-red-700 dark:text-red-300">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const handleBack = () => {
    const returnUrl = sessionStorage.getItem('adminUsersReturnUrl');
    sessionStorage.removeItem('adminUsersReturnUrl');
    navigate(returnUrl || '/admin/users', { state: { refresh: Date.now() } });
  };

  const isCurrentUser = currentUser?.id === userId;

  return (
    <div className="space-y-6">
      {/* Back Link */}
      <button
        onClick={handleBack}
        className="inline-flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Users
      </button>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3 text-red-700 dark:text-red-300">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* User Header */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900/50 flex items-center justify-center text-primary-600 dark:text-primary-400 text-2xl font-semibold">
              {user.first_name?.[0]}{user.last_name?.[0]}
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white">
                  {user.first_name} {user.last_name}
                </h1>
                <span className={`px-2 py-1 text-xs font-medium rounded ${roleBadges[user.role] || roleBadges.user}`}>
                  {user.role.replace('_', ' ')}
                </span>
                {user.is_internal_staff && (
                  <span className="px-2 py-1 text-xs font-medium bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 rounded">
                    Internal Staff
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-neutral-600 dark:text-neutral-300">
                <div className="flex items-center gap-1">
                  <Mail className="w-4 h-4" />
                  {user.email}
                </div>
                {user.phone && (
                  <div className="flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    {user.phone}
                  </div>
                )}
                {(user.cw_company_id || user.company_name) && (
                  <Link
                    to={`/admin/companies/${user.cw_company_id}`}
                    className="flex items-center gap-1 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                  >
                    <Building2 className="w-4 h-4" />
                    {user.company_name || `Company #${user.cw_company_id}`}
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowEditModal(true)}
              className="px-3 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-200 bg-white dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-600 flex items-center gap-2"
            >
              <Pencil className="w-4 h-4" />
              Edit
            </button>
            {!isCurrentUser && (
              <>
                <button
                  onClick={() => setShowPasswordModal(true)}
                  className="px-3 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-200 bg-white dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-600 flex items-center gap-2"
                >
                  <Key className="w-4 h-4" />
                  Reset Password
                </button>
                <button
                  onClick={() => setShowRoleModal(true)}
                  className="px-3 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-200 bg-white dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-600 flex items-center gap-2"
                >
                  <Shield className="w-4 h-4" />
                  Change Role
                </button>
              </>
            )}
          </div>
        </div>

        {/* User Details Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-700">
          <div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Status</p>
            <div className="flex items-center gap-2 mt-1">
              {user.email_verified ? (
                <>
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm font-medium text-green-600 dark:text-green-400">Verified</span>
                </>
              ) : (
                <>
                  <Clock className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm font-medium text-yellow-600 dark:text-yellow-400">Pending</span>
                </>
              )}
            </div>
          </div>
          <div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Last Login</p>
            <p className="text-sm font-medium text-neutral-900 dark:text-white mt-1">
              {formatDate(user.last_login)}
            </p>
          </div>
          <div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Joined</p>
            <p className="text-sm font-medium text-neutral-900 dark:text-white mt-1">
              {formatDate(user.created_at)}
            </p>
          </div>
          <div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Active Sessions</p>
            <p className="text-sm font-medium text-neutral-900 dark:text-white mt-1">
              {sessions.length}
            </p>
          </div>
          <div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Login Status</p>
            <div className="flex items-center gap-2 mt-1">
              {user.failed_login_attempts >= 5 ? (
                <>
                  <Lock className="w-4 h-4 text-red-500" />
                  <span className="text-sm font-medium text-red-600 dark:text-red-400">
                    Locked ({user.failed_login_attempts} attempts)
                  </span>
                </>
              ) : user.failed_login_attempts > 0 ? (
                <>
                  <AlertCircle className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm font-medium text-yellow-600 dark:text-yellow-400">
                    {user.failed_login_attempts} failed attempt{user.failed_login_attempts !== 1 ? 's' : ''}
                  </span>
                </>
              ) : (
                <>
                  <Unlock className="w-4 h-4 text-green-500" />
                  <span className="text-sm font-medium text-green-600 dark:text-green-400">OK</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Locked Account Warning */}
        {user.failed_login_attempts >= 5 && !isCurrentUser && (
          <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-red-500" />
              <div>
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  Account is locked due to {user.failed_login_attempts} failed login attempts
                </p>
                {user.last_failed_login && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
                    Last failed attempt: {formatDate(user.last_failed_login)}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={handleUnlockAccount}
              disabled={unlocking}
              className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50 flex items-center gap-2"
            >
              {unlocking ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Unlocking...
                </>
              ) : (
                <>
                  <Unlock className="w-4 h-4" />
                  Unlock Account
                </>
              )}
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Sessions */}
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-medium text-neutral-900 dark:text-white">Active Sessions</h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">{sessions.length} active session{sessions.length !== 1 ? 's' : ''}</p>
            </div>
            {sessions.length > 0 && !isCurrentUser && (
              <button
                onClick={() => setRevokeAllModal(true)}
                className="px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg"
              >
                Revoke All
              </button>
            )}
          </div>
          <div className="divide-y divide-neutral-200 dark:divide-neutral-700">
            {sessions.length === 0 ? (
              <div className="px-6 py-8 text-center text-neutral-500 dark:text-neutral-400">
                <Activity className="w-8 h-8 mx-auto mb-2 text-neutral-400 dark:text-neutral-500" />
                No active sessions
              </div>
            ) : (
              sessions.map((session) => (
                <div key={session.sid} className="px-6 py-4 flex items-center justify-between">
                  <div>
                    <code className="text-xs text-neutral-600 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-700 px-2 py-1 rounded">
                      {session.sid.substring(0, 16)}...
                    </code>
                    <div className="flex items-center gap-2 mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                      <Clock className="w-3 h-3" />
                      Expires in {getTimeRemaining(session.expire)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
            <h2 className="text-lg font-medium text-neutral-900 dark:text-white">Recent Activity</h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">Last {activity.length} actions</p>
          </div>
          <div className="divide-y divide-neutral-200 dark:divide-neutral-700 max-h-96 overflow-y-auto">
            {activity.length === 0 ? (
              <div className="px-6 py-8 text-center text-neutral-500 dark:text-neutral-400">
                <Shield className="w-8 h-8 mx-auto mb-2 text-neutral-400 dark:text-neutral-500" />
                No activity recorded
              </div>
            ) : (
              activity.map((entry) => (
                <div key={entry.id} className="px-6 py-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-neutral-900 dark:text-white">
                      {actionLabels[entry.action] || entry.action}
                    </p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      {formatDate(entry.created_at)}
                    </p>
                  </div>
                  {entry.ip_address && (
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                      IP: {entry.ip_address}
                    </p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* ConnectWise Member Mapping (optional, but enables native author attribution) */}
      {user.is_internal_staff && (
        <CwMemberMappingCard userId={user.id} />
      )}

      {/* Company Access (All Users) */}
      <div className={`grid grid-cols-1 ${user.is_internal_staff ? 'lg:grid-cols-2' : ''} gap-6`}>
        {/* Company Access */}
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-medium text-neutral-900 dark:text-white">Company Access</h2>
              <CompanyAccessSummary
                count={companyAccess.length}
                isInternalStaff={user.is_internal_staff}
                hasAllCompanies={hasAllCompanies}
              />
            </div>
            <button
              onClick={() => setShowAddCompanyModal(true)}
              className="px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Add
            </button>
          </div>
          <div className="divide-y divide-neutral-200 dark:divide-neutral-700 max-h-64 overflow-y-auto">
            {loadingCompanyAccess ? (
              <div className="px-6 py-8 text-center">
                <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : companyAccess.length === 0 ? (
              <div className="px-6 py-8 text-center text-neutral-500 dark:text-neutral-400">
                <Building2 className="w-8 h-8 mx-auto mb-2 text-neutral-400 dark:text-neutral-500" />
                {user.is_internal_staff ? (
                  hasAllCompanies ? (
                    <>
                      <p className="text-sm">No restrictions applied</p>
                      <p className="text-xs mt-1">Role has "all_companies" - user can access all companies</p>
                      <p className="text-xs text-primary-500 mt-2">Add companies to restrict access</p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm">No companies assigned</p>
                      <p className="text-xs mt-1">User cannot access company-specific data</p>
                    </>
                  )
                ) : (
                  <>
                    <p className="text-sm">No additional companies assigned</p>
                    <p className="text-xs mt-1">User can only access their primary company data</p>
                  </>
                )}
              </div>
            ) : (
              companyAccess.map((company) => (
                <div key={company.id} className="px-6 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-neutral-900 dark:text-white">
                      {company.companyName || `Company #${company.cwCompanyId}`}
                    </p>
                    {company.assignedBy && (
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        Added by {company.assignedBy.firstName || company.assignedBy.email}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* The role held at this company; blank = the user's own role */}
                    {!user.is_internal_staff && (
                      <select
                        value={company.role || ''}
                        onChange={(e) => handleCompanyRoleChange(company.cwCompanyId, e.target.value || null)}
                        className="text-xs border border-neutral-300 dark:border-neutral-600 rounded px-2 py-1 bg-white dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200"
                        title="Role at this company"
                      >
                        <option value="">Same as their role ({roleLabel(user.role)})</option>
                        {customerRoles.map((r) => (
                          <option key={r.name} value={r.name}>{r.displayName}</option>
                        ))}
                      </select>
                    )}
                    {/* Set as Primary button - only for customer users */}
                    {!user.is_internal_staff && (
                      <button
                        onClick={() => handleSetPrimaryCompany(company.cwCompanyId, company.companyName)}
                        className="px-2 py-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded"
                        title="Set as primary company"
                      >
                        Set as Primary
                      </button>
                    )}
                    <button
                      onClick={() => handleRemoveCompany(company.cwCompanyId)}
                      className="p-1.5 text-neutral-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Location Access (Internal Staff Only) */}
        {user.is_internal_staff && (
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-neutral-900 dark:text-white">Location Access</h2>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  {locationAccess.length === 0 ? 'Access to all locations' : `${locationAccess.length} assigned locations`}
                </p>
              </div>
              <button
                onClick={() => setShowAddLocationModal(true)}
                className="px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </div>
            <div className="divide-y divide-neutral-200 dark:divide-neutral-700 max-h-64 overflow-y-auto">
              {loadingLocationAccess ? (
                <div className="px-6 py-8 text-center">
                  <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : locationAccess.length === 0 ? (
                <div className="px-6 py-8 text-center text-neutral-500 dark:text-neutral-400">
                  <Info className="w-8 h-8 mx-auto mb-2 text-blue-400" />
                  <p className="text-sm">No location restrictions</p>
                  <p className="text-xs mt-1">User can access data from all locations</p>
                </div>
              ) : (
                locationAccess.map((location) => (
                  <div key={location.id} className="px-6 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-neutral-900 dark:text-white">
                        {location.locationName || `Location #${location.cwLocationId}`}
                      </p>
                      {location.assignedBy && (
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">
                          Added by {location.assignedBy.firstName || location.assignedBy.email}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleRemoveLocation(location.cwLocationId)}
                      className="p-1.5 text-neutral-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Board Access (Internal Staff Only) */}
        {user.is_internal_staff && (
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-neutral-900 dark:text-white">Board Access</h2>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  {boardAccess.length === 0 ? 'Access to all boards' : `${boardAccess.length} assigned boards`}
                </p>
              </div>
              <button
                onClick={() => setShowAddBoardModal(true)}
                className="px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </div>
            <div className="divide-y divide-neutral-200 dark:divide-neutral-700 max-h-64 overflow-y-auto">
              {loadingBoardAccess ? (
                <div className="px-6 py-8 text-center">
                  <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : boardAccess.length === 0 ? (
                <div className="px-6 py-8 text-center text-neutral-500 dark:text-neutral-400">
                  <Info className="w-8 h-8 mx-auto mb-2 text-blue-400" />
                  <p className="text-sm">No board restrictions</p>
                  <p className="text-xs mt-1">User can access tickets from all boards</p>
                </div>
              ) : (
                boardAccess.map((board) => (
                  <div key={board.id} className="px-6 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-neutral-900 dark:text-white">
                        {board.boardName || `Board #${board.cwBoardId}`}
                      </p>
                      {board.assignedBy && (
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">
                          Added by {board.assignedBy.firstName || board.assignedBy.email}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleRemoveBoard(board.cwBoardId)}
                      className="p-1.5 text-neutral-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add Company Modal */}
      {showAddCompanyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowAddCompanyModal(false)} />
          <div className="relative bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center">
                <Building2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Add Company Access</h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  Search for a company to grant access
                </p>
              </div>
            </div>

            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <input
                  type="text"
                  value={companySearchQuery}
                  onChange={(e) => setCompanySearchQuery(e.target.value)}
                  placeholder="Search companies..."
                  className="w-full pl-10 pr-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                  autoFocus
                />
              </div>
            </div>

            <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg max-h-64 overflow-y-auto">
              {searchingCompanies ? (
                <div className="p-4 text-center">
                  <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : companySearchResults.length === 0 ? (
                <div className="p-4 text-center text-neutral-500 dark:text-neutral-400 text-sm">
                  {companySearchQuery.length < 2 ? 'Type at least 2 characters to search' : 'No companies found'}
                </div>
              ) : (
                companySearchResults.map((company) => (
                  <button
                    key={company.cwCompanyId || company.cw_company_id || company.id}
                    onClick={() => handleAddCompany(company)}
                    disabled={addingCompany}
                    className="w-full px-4 py-3 text-left hover:bg-neutral-50 dark:hover:bg-neutral-700 flex items-center justify-between border-b border-neutral-200 dark:border-neutral-700 last:border-b-0"
                  >
                    <div>
                      <p className="text-sm font-medium text-neutral-900 dark:text-white">
                        {company.companyName || company.company_name || company.name}
                      </p>
                      <p className="text-xs text-neutral-500">
                        ID: {company.cwCompanyId || company.cw_company_id || company.id}
                      </p>
                    </div>
                    <Plus className="w-4 h-4 text-primary-500" />
                  </button>
                ))
              )}
            </div>

            <div className="flex justify-end mt-4">
              <button
                onClick={() => {
                  setShowAddCompanyModal(false);
                  setCompanySearchQuery('');
                  setCompanySearchResults([]);
                }}
                className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Location Modal */}
      {showAddLocationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowAddLocationModal(false)} />
          <div className="relative bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center">
                <Building2 className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Add Location Access</h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  Select a location to restrict access
                </p>
              </div>
            </div>

            <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg max-h-64 overflow-y-auto">
              {availableLocations.length === 0 ? (
                <div className="p-4 text-center text-neutral-500 dark:text-neutral-400 text-sm">
                  No locations available
                </div>
              ) : (
                availableLocations
                  .filter(loc => !locationAccess.some(la => la.cwLocationId === loc.id))
                  .map((location) => (
                    <button
                      key={location.id}
                      onClick={() => handleAddLocation(location)}
                      disabled={addingLocation}
                      className="w-full px-4 py-3 text-left hover:bg-neutral-50 dark:hover:bg-neutral-700 flex items-center justify-between border-b border-neutral-200 dark:border-neutral-700 last:border-b-0"
                    >
                      <div>
                        <p className="text-sm font-medium text-neutral-900 dark:text-white">
                          {location.name}
                        </p>
                        <p className="text-xs text-neutral-500">
                          ID: {location.id}
                        </p>
                      </div>
                      <Plus className="w-4 h-4 text-primary-500" />
                    </button>
                  ))
              )}
              {availableLocations.length > 0 &&
               availableLocations.filter(loc => !locationAccess.some(la => la.cwLocationId === loc.id)).length === 0 && (
                <div className="p-4 text-center text-neutral-500 dark:text-neutral-400 text-sm">
                  All locations already assigned
                </div>
              )}
            </div>

            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowAddLocationModal(false)}
                className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Board Modal */}
      {showAddBoardModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowAddBoardModal(false)} />
          <div className="relative bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/50 rounded-full flex items-center justify-center">
                <Shield className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Add Board Access</h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  Select a board to restrict access
                </p>
              </div>
            </div>

            <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg max-h-64 overflow-y-auto">
              {availableBoards.length === 0 ? (
                <div className="p-4 text-center text-neutral-500 dark:text-neutral-400 text-sm">
                  No boards available
                </div>
              ) : (
                availableBoards
                  .filter(board => !boardAccess.some(ba => ba.cwBoardId === board.id))
                  .map((board) => (
                    <button
                      key={board.id}
                      onClick={() => handleAddBoard(board)}
                      disabled={addingBoard}
                      className="w-full px-4 py-3 text-left hover:bg-neutral-50 dark:hover:bg-neutral-700 flex items-center justify-between border-b border-neutral-200 dark:border-neutral-700 last:border-b-0"
                    >
                      <div>
                        <p className="text-sm font-medium text-neutral-900 dark:text-white">
                          {board.name}
                        </p>
                        <p className="text-xs text-neutral-500">
                          ID: {board.id}
                        </p>
                      </div>
                      <Plus className="w-4 h-4 text-primary-500" />
                    </button>
                  ))
              )}
              {availableBoards.length > 0 &&
               availableBoards.filter(board => !boardAccess.some(ba => ba.cwBoardId === board.id)).length === 0 && (
                <div className="p-4 text-center text-neutral-500 dark:text-neutral-400 text-sm">
                  All boards already assigned
                </div>
              )}
            </div>

            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowAddBoardModal(false)}
                className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowEditModal(false)} />
          <div className="relative bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/50 rounded-full flex items-center justify-center">
                <Pencil className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Edit Profile</h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  Update profile for {user.email}
                </p>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={editForm.firstName}
                  onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={editForm.lastName}
                  onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleEditProfile}
                disabled={savingEdit}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50"
              >
                {savingEdit ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => !passwordSuccess && setShowPasswordModal(false)} />
          <div className="relative bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
            {passwordSuccess ? (
              <div className="text-center py-4">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">Password Reset</h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-300">
                  Password has been reset. User will need to log in again.
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/50 rounded-full flex items-center justify-center">
                    <Key className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Reset Password</h3>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">
                      Set new password for {user.email}
                    </p>
                  </div>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-4">
                  <p className="text-sm text-yellow-800 dark:text-yellow-300">
                    This will log the user out of all devices and require them to log in with the new password.
                  </p>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={passwordForm.password}
                        onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
                        className="w-full px-3 py-2 pr-10 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                        placeholder="Minimum 8 characters"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Confirm Password
                    </label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                      placeholder="Confirm password"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowPasswordModal(false);
                      setPasswordForm({ password: '', confirmPassword: '' });
                    }}
                    className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleResetPassword}
                    disabled={savingPassword || !passwordForm.password || !passwordForm.confirmPassword}
                    className="px-4 py-2 text-sm font-medium text-white bg-yellow-500 rounded-lg hover:bg-yellow-600 disabled:opacity-50"
                  >
                    {savingPassword ? 'Resetting...' : 'Reset Password'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Role Change Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowRoleModal(false)} />
          <div className="relative bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center">
                <Shield className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Change Role</h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  Update role for {user.email}
                </p>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              {allRoles.map(({ name: role, displayName, isInternal }) => (
                <label
                  key={role}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedRole === role
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                      : 'border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-700'
                  }`}
                >
                  <input
                    type="radio"
                    name="role"
                    value={role}
                    checked={selectedRole === role}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    className="sr-only"
                  />
                  <span className={`px-2 py-1 text-xs font-medium rounded ${roleBadges[role] || roleBadges.end_user}`}>
                    {displayName || role}
                  </span>
                  {isInternal && (
                    <span className="text-xs text-orange-600 dark:text-orange-400">Internal Staff</span>
                  )}
                </label>
              ))}
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowRoleModal(false)}
                className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleRoleChange}
                disabled={updatingRole || selectedRole === user.role}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50"
              >
                {updatingRole ? 'Updating...' : 'Update Role'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke All Sessions Modal */}
      {revokeAllModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setRevokeAllModal(false)} />
          <div className="relative bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/50 rounded-full flex items-center justify-center">
                <LogOut className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Revoke All Sessions</h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  This will log the user out of all devices
                </p>
              </div>
            </div>

            <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-6">
              Are you sure you want to revoke all {sessions.length} session{sessions.length !== 1 ? 's' : ''} for{' '}
              <strong>{user.email}</strong>? They will need to log in again on all devices.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setRevokeAllModal(false)}
                className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleRevokeAllSessions}
                disabled={revokingAll}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                {revokingAll ? 'Revoking...' : 'Revoke All Sessions'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
